'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useClerk, useSignIn, useSignUp } from '@clerk/nextjs';

// Shown on /signup and /login above Clerk's form when we know the purchase email
// (from the link or the purchase claim cookie). Same Google flow as Clerk's own
// button, plus Google's login hint, so Google offers the account the buyer paid
// with first instead of whichever account happens to be signed in.
// It replaces Clerk's own social buttons on that page (see
// HIDE_CLERK_SOCIAL_BUTTONS), so the buyer sees one Google button, labelled with
// the email it suggests. Google still lets them pick another account.
//
// Each mode returns to its own callback page, never Clerk's /sso-callback paths,
// which the <SignUp/> and <SignIn/> catch-alls keep handling for Clerk's buttons.

const GOOGLE_SIGNUP_CALLBACK_URL = '/signup/google-callback';
const GOOGLE_LOGIN_CALLBACK_URL = '/login/google-callback';
const AFTER_SIGNUP_URL = '/onboarding/questions';
type Props = {
  email: string;
  mode: 'signUp' | 'signIn';
  /** Where to land once signed in (sign in only; sign up goes to onboarding). */
  afterSignInUrl?: string;
};
export default function GoogleLoginHintButton(t0) {
  const $ = _c(28);
  const {
    email,
    mode,
    afterSignInUrl: t1
  } = t0;
  const afterSignInUrl = t1 === undefined ? "/" : t1;
  const clerk = useClerk();
  const signUp = useSignUp();
  const signIn = useSignIn();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const isLoaded = mode === "signUp" ? signUp.isLoaded : signIn.isLoaded;
  let t2;
  if ($[0] !== afterSignInUrl || $[1] !== clerk || $[2] !== email || $[3] !== mode || $[4] !== signIn || $[5] !== signUp) {
    t2 = async () => {
      if (mode === "signUp" && signUp.isLoaded) {
        const attempt = await signUp.signUp.create({
          strategy: "oauth_google",
          redirectUrl: clerk.buildUrlWithAuth(GOOGLE_SIGNUP_CALLBACK_URL),
          actionCompleteRedirectUrl: AFTER_SIGNUP_URL,
          oidcLoginHint: email
        });
        return attempt.verifications.externalAccount.externalVerificationRedirectURL;
      }
      if (mode === "signIn" && signIn.isLoaded) {
        const attempt_0 = await signIn.signIn.create({
          strategy: "oauth_google",
          redirectUrl: clerk.buildUrlWithAuth(GOOGLE_LOGIN_CALLBACK_URL),
          actionCompleteRedirectUrl: afterSignInUrl,
          oidcLoginHint: email
        });
        return attempt_0.firstFactorVerification.externalVerificationRedirectURL;
      }
      return null;
    };
    $[0] = afterSignInUrl;
    $[1] = clerk;
    $[2] = email;
    $[3] = mode;
    $[4] = signIn;
    $[5] = signUp;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const googleUrl = t2;
  let t3;
  if ($[7] !== busy || $[8] !== googleUrl || $[9] !== isLoaded) {
    t3 = async () => {
      if (!isLoaded || busy) {
        return;
      }
      setBusy(true);
      setFailed(false);
      const url = await googleUrl().catch(_temp);
      if (!url) {
        setFailed(true);
        setBusy(false);
        return;
      }
      window.location.assign(url.toString());
    };
    $[7] = busy;
    $[8] = googleUrl;
    $[9] = isLoaded;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const start = t3;
  const t4 = !isLoaded || busy;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <svg className="size-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.4-.4-3.5z" /></svg>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = busy ? "Opening Google..." : "Continue with Google";
  let t7;
  if ($[12] !== t6) {
    t7 = <span>{t6}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== email) {
    t8 = <span className="max-w-full truncate text-xs font-normal text-gray-500">{email}</span>;
    $[14] = email;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <span className="flex min-w-0 flex-col items-start leading-tight">{t7}{t8}</span>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== start || $[20] !== t4 || $[21] !== t9) {
    t10 = <button type="button" onClick={start} disabled={t4} className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">{t5}{t9}</button>;
    $[19] = start;
    $[20] = t4;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== failed) {
    t11 = failed && <p className="mt-2 text-center text-xs text-red-600">Google did not open. Try again, or use the form below.</p>;
    $[23] = failed;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="mb-4 w-full">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
function _temp(error) {
  console.error("[GoogleLoginHintButton] Google flow failed to start", error);
  return null;
}